import { getTooltipAttributes } from "@common/contexts/Tooltip";
import DragHandle from "@common/controls/DragHandle/component";
import { NO_VALUE, isColumnVisible } from "@common/controls/Grid/lib";
import { IconAction } from "@common/controls/Icon/component";
import { FancyCheckbox } from "@common/controls/Inputs/Checkbox/component";
import { type TreeViewExpandMode } from "@common/controls/TreeView/Dnd/Inner/types";
import { IconType } from "@common/declarations";
import { SortableElementHOC, type SortableElementHOCAddedProps } from "@common/hocs/dnd";
import { useI18n } from "@common/hooks/i18n";
import classnames from "classnames";
import React from "react";
import { type GridColumn, type GridGroupInfo } from "../types";
import { RowContext, type RowContextProps } from "./context";
import { renderCellValue, renderTooltip } from "./lib";

export type RowProps<T extends {}> = Partial<Omit<SortableElementHOCAddedProps, "data" | "onClick">> & {
  className?: string;
  columns: GridColumn<T>[];
  data: T;
  dragMode?: "handle" | "row";
  expandMode?: TreeViewExpandMode;
  id: string | number;
  idKey: Extract<keyof T, string>;
  indent?: number;
  expand?: "hidden" | "expand" | "collapse";
  rowMenu?: React.ReactNode;
  hasMenu?: boolean;
  selectedItems?: T[];
  style?: React.CSSProperties;
  tooltip?: string | null;

  isMenuAvailable?: (rowData: T) => boolean;
  isRowHighlighted?: (rowData: T) => boolean;
  onCheckboxClick?: (isSelected: boolean, rowData?: T[]) => void;
  onClick?: (rowData: T) => void;
  toggleExpanded?: () => void;
};

const RowInner = <T extends {}>(props: RowProps<T>) => {
  const i18n = useI18n();

  const contextValue = React.useMemo<RowContextProps<T>>(
    () => ({
      rowId: props.id,
      rowData: props.data
    }),
    [props.id, props.data]
  );

  const isMenuAvailable = !props.isMenuAvailable || !!props.isMenuAvailable(props.data);

  const isSelected = React.useMemo(
    () => (props.selectedItems ?? []).some((x) => x[props.idKey] === props.data[props.idKey]),
    [props.selectedItems, props.idKey, props.data]
  );

  const rowProps = {
    className: classnames("grid_row", props.className, {
      highlight: (props.isRowHighlighted && props.isRowHighlighted(props.data)) || isSelected
    }),
    onClick: () => {
      props.expandMode === "row" && props.toggleExpanded && props.toggleExpanded();
      isMenuAvailable && props.onClick && props.onClick(props.data);
    },
    id: props.id?.toString(),
    ref: props.setRef,
    style: props.style
  };

  if (props.dragMode === "row") {
    Object.assign(rowProps, props.handleProps);
  }

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...rowProps} {...getTooltipAttributes(props.tooltip)}>
        {props.dragMode === "handle" && <DragHandle tag="td" {...props.handleProps} />}
        {!!props.onCheckboxClick && !!props.selectedItems && (
          <td
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <FancyCheckbox
              value={isSelected}
              onChange={(value) => {
                props.onCheckboxClick!(value ?? false, [props.data]);
              }}
            />
          </td>
        )}
        {props.columns.filter(isColumnVisible).map((col, i) => {
          const hoverValue = col.hoverData ? col.hoverData(props.data) : null;
          return (
            <td
              key={col.key as string}
              className={classnames(col.className, "cell", {
                "has-hover-value": hoverValue !== null
              })}
              style={{
                width: col.width
              }}
              data-name={col.qa || col.key}
            >
              <div
                className={classnames("cell_content", col.align ? `align_${col.align}` : "")}
                style={{ paddingLeft: i === 0 ? props.indent : 0 }}
              >
                {i === 0 && !!props.expand && (
                  <IconAction
                    size={16}
                    className="toggle"
                    onClick={(e) => (e.stopPropagation(), props.toggleExpanded!())}
                    icon={props.expand === "collapse" ? IconType.ChevronUp : IconType.ChevronDown}
                    style={{ visibility: props.expand === "hidden" ? "hidden" : undefined }}
                  />
                )}

                {renderCellValue(i18n, col, props.data)}
                {hoverValue !== null && (
                  <span className="grid-hover-field-value">
                    {typeof hoverValue === "string" ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: hoverValue ?? NO_VALUE
                        }}
                      />
                    ) : (
                      hoverValue ?? NO_VALUE
                    )}
                    {renderTooltip(col, props.data)}
                  </span>
                )}
              </div>
            </td>
          );
        })}

        {props.hasMenu && (
          <td className={classnames("actions cell")}>
            <div
              className="cell_content"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {isMenuAvailable && props.rowMenu}
            </div>
          </td>
        )}
      </tr>
    </RowContext.Provider>
  );
};

export const Row = <T extends {}>(props: RowProps<T>) => <RowInner {...props} />;

export const SortableRow = SortableElementHOC<RowProps<any>>(Row);

export interface GroupProps<T> {
  colSpan: number;
  hasMenu?: boolean;
  groupMenu?: React.ReactNode;
  data: GridGroupInfo;
  style?: React.CSSProperties;

  isMenuAvailable?: (rowData: any) => boolean;
  isRowHighlighted?: (rowData: T) => boolean;
  onClick?: (rowData: any) => void;
  render?: (data: GridGroupInfo) => any;
}
export const Group = <T extends {}>(
  props: GroupProps<T> & Partial<Omit<SortableElementHOCAddedProps, "data" | "onClick">>
) => {
  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLTableRowElement>) => {
      if (props.onClick && (!props.isMenuAvailable || props.isMenuAvailable(props.data))) {
        props.onClick(props.data);
      }
    },
    [props.onClick, props.data, props.isMenuAvailable]
  );

  return (
    <RowContext.Provider value={{ rowId: props.data.id, rowData: props.data }}>
      <tr
        ref={props.setRef}
        className={classnames("grid_row group", {
          clickable:
            !!props.onClick &&
            ((props.isMenuAvailable && !!props.isMenuAvailable(props.data)) || !props.isMenuAvailable)
        })}
        onClick={onClick}
        id={props.data.id}
        style={props.style}
      >
        <td colSpan={props.colSpan - 1}>{props.render ? props.render(props.data) : props.data.label}</td>

        {props.hasMenu && (
          <td className={classnames("cell actions")}>
            <div className="cell_content">
              {(!props.isMenuAvailable || props.isMenuAvailable(props.data)) && props.groupMenu}
            </div>
          </td>
        )}
      </tr>
    </RowContext.Provider>
  );
};
